import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../../firebase';
import { doc, setDoc, serverTimestamp, collection, query, where, getDocs, getDoc } from 'firebase/firestore';
import {
    MdChevronLeft,
    MdChevronRight,
    MdExpandMore,
    MdExpandLess,
    MdSave,
    MdQrCodeScanner,
    MdClose,
    MdMap,
    MdMyLocation
} from 'react-icons/md';
import { QRCodeCanvas } from 'qrcode.react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import { Map, AdvancedMarker, useMap, useMapsLibrary, APIProvider } from '@vis.gl/react-google-maps';

// --- POLYLINE SEGÉDKOMPONENS ---
const Polyline = (props) => {
  const map = useMap();
  const mapsLib = useMapsLibrary('maps');
  const polylineRef = useRef(null);

  useEffect(() => {
    if (!map || !mapsLib || !props.path) return;

    if (polylineRef.current) {
        polylineRef.current.setMap(null);
    }

    polylineRef.current = new mapsLib.Polyline({
      path: props.path,
      geodesic: true,
      strokeColor: props.strokeColor || '#FF0000',
      strokeOpacity: 1.0,
      strokeWeight: 5,
    });

    polylineRef.current.setMap(map);

    return () => {
      if (polylineRef.current) polylineRef.current.setMap(null);
    };
  }, [map, mapsLib, props.path, props.strokeColor]);

  return null;
};

const CompetitionScoringWeb = () => {
    const navigate = useNavigate();

    // --- STATE ---
    const [competitors, setCompetitors] = useState([]);
    const [allShots, setAllShots] = useState({}); // { competitorId: [ { value, target, sum, position }, ... ] }
    const [currentCompetitorIndex, setCurrentCompetitorIndex] = useState(0);
    const [currentSeriesGlobal, setCurrentSeriesGlobal] = useState(1);
    const [startingTarget, setStartingTarget] = useState(1);
    const [competitionId, setCompetitionId] = useState(null);
    const [isClosed, setIsClosed] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [showQrModal, setShowQrModal] = useState(false);
    const [showFinalizeConfirmModal, setShowFinalizeConfirmModal] = useState(false);
    const [qrStringsList, setQrStringsList] = useState([]);
    const [currentQrIndex, setCurrentQrIndex] = useState(0);
    const [qrSpeedMs, setQrSpeedMs] = useState(500);
    const [isAverageInPercentage, setIsAverageInPercentage] = useState(false);

    // --- MAP & GPS STATE ---
    const [showMap, setShowMap] = useState(false);
    const [courseMapData, setCourseMapData] = useState(null);
    const [userLocation, setUserLocation] = useState(null);
    const [eventLocationFallback, setEventLocationFallback] = useState(null);
    const [isSearchingEvent, setIsSearchingEvent] = useState(false);
    const mapRef = useRef(null);

    // GPS KÖVETÉS
    useEffect(() => {
        if (!("geolocation" in navigator)) return;

        const watchId = navigator.geolocation.watchPosition(
            (pos) => {
                setUserLocation({
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude
                });
            },
            (err) => console.warn("GPS hiba:", err),
            { enableHighAccuracy: true, maximumAge: 10000 }
        );

        return () => navigator.geolocation.clearWatch(watchId);
    }, []);

    // PÁLYA KERESÉS ÉS BETÖLTÉS
    const loadCourseMapData = async (eventId) => {
        if (!eventId) return;
        try {
            console.log("Pályatérkép lekérése:", eventId);
            const docRef = doc(db, "events", eventId);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data.courseMapData) {
                    setCourseMapData(data.courseMapData);
                    console.log("Pályatérkép betöltve.");
                }
                if (data.gpsLatitude && data.gpsLongitude) {
                    setEventLocationFallback({
                        lat: parseFloat(data.gpsLatitude),
                        lng: parseFloat(data.gpsLongitude)
                    });
                }
            }
        } catch (e) {
            console.error("Hiba a pálya betöltésekor:", e);
        }
    };

    const findNearbyEvent = async () => {
        if (!userLocation || isSearchingEvent || courseMapData) return;

        setIsSearchingEvent(true);
        try {
            const today = new Date().toISOString().split('T')[0];
            const q = query(
                collection(db, "events"),
                where("status", "==", "Aktív"),
                where("competitionDate", "==", today)
            );

            const querySnapshot = await getDocs(q);
            let bestMatch = null;
            let minDistance = 5000; // 5km

            const expectedTargets = competitors[0]?.targetCount || 0;

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                const lat = parseFloat(data.gpsLatitude);
                const lng = parseFloat(data.gpsLongitude);
                const targets = parseInt(data.numberOfTargets) || 0;

                if (!isNaN(lat) && !isNaN(lng)) {
                    // Egyszerűsített távolság számítás (Pitagorasz közeli távolságon oké)
                    const d = Math.sqrt(Math.pow(lat - userLocation.lat, 2) + Math.pow(lng - userLocation.lng, 2)) * 111000;

                    if (d < minDistance && targets === expectedTargets) {
                        minDistance = d;
                        bestMatch = doc.id;
                    }
                }
            });

            if (bestMatch) {
                console.log("Közeli verseny találat:", bestMatch);
                await loadCourseMapData(bestMatch);
            }
        } catch (e) {
            console.error("Hiba a közeli verseny keresésekor:", e);
        } finally {
            setIsSearchingEvent(false);
        }
    };

    // Automata keresés ha nincs adat (Háttérben is, nem csak ha showMap true)
    useEffect(() => {
        if (!courseMapData && userLocation && competitors.length > 0) {
            findNearbyEvent();
        }
    }, [courseMapData, userLocation, competitors.length]);

    // --- BÖNGÉSZŐ VISSZA GOMB KEZELÉSE A TÉRKÉPHEZ ---
    useEffect(() => {
        if (showMap) {
            // Amikor megnyílik a térkép, teszünk egy virtuális bejegyzést a history-ba
            window.history.pushState({ mapOpen: true }, "");

            const handlePopState = (event) => {
                // Ha a felhasználó a telefon vissza gombját nyomja, csak zárjuk be a térképet
                setShowMap(false);
            };

            window.addEventListener('popstate', handlePopState);
            return () => {
                window.removeEventListener('popstate', handlePopState);
            };
        }
    }, [showMap]);

    const handleCloseMap = () => {
        // Ha az X gombbal zárjuk be, "kézzel" visszalépünk a history-ban egyet,
        // ami kiváltja a popstate-et vagy csak törli a pushState-et.
        if (showMap) {
            window.history.back();
            setShowMap(false);
        }
    };

    // --- LOADING & PERSISTENCE ---
    useEffect(() => {
        if (competitors.length > 0) return;

        const savedSession = localStorage.getItem('competition_scoring_session');
        if (savedSession) {
            try {
                const session = JSON.parse(savedSession);
                const now = Date.now();
                const lastUpdated = session.lastUpdated || session.timestamp || 0;
                const TWELVE_HOURS = 12 * 60 * 60 * 1000;

                // 12 órán belüli munkamenet (aktív vagy lezárt) automatikus visszaállítása
                if (!lastUpdated || (now - lastUpdated < TWELVE_HOURS)) {
                    setCompetitors(session.competitors);
                    setAllShots(session.allShots);
                    setCurrentSeriesGlobal(session.currentSeriesGlobal);
                    setCurrentCompetitorIndex(session.currentCompetitorIndex || 0);
                    setStartingTarget(session.startingTarget);
                    setCompetitionId(session.competitionId);
                    setIsClosed(session.isClosed || false);
                    if (session.courseMapData) setCourseMapData(session.courseMapData);
                    if (session.eventLocationFallback) setEventLocationFallback(session.eventLocationFallback);
                    return;
                } else {
                    // 12 óránál régebbi munkamenet automatikus törlése
                    localStorage.removeItem('competition_scoring_session');
                }
            } catch (e) {
                console.error("Hiba a mentett munkamenet betöltésekor:", e);
            }
        }

        const tempCompetitors = localStorage.getItem('temp_competition_competitors');
        const tempTarget = localStorage.getItem('temp_competition_starting_target');

        if (tempCompetitors) {
            const comps = JSON.parse(tempCompetitors);
            if (comps.length > 0) {
                setCompetitors(comps);
                setStartingTarget(parseInt(tempTarget) || 1);

                const initialShots = {};
                comps.forEach(c => {
                    initialShots[c.competitorId] = [];
                });
                setAllShots(initialShots);
            }
        } else {
            const timer = setTimeout(() => {
                if (competitors.length === 0) {
                    navigate('/');
                }
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [navigate, competitors.length]);

    useEffect(() => {
        if (competitors.length > 0) {
            const session = {
                competitors,
                allShots,
                currentSeriesGlobal,
                currentCompetitorIndex,
                startingTarget,
                competitionId,
                isClosed,
                courseMapData,
                eventLocationFallback,
                lastUpdated: Date.now() // Időbélyeg hozzáadása
            };
            localStorage.setItem('competition_scoring_session', JSON.stringify(session));
        }
    }, [competitors, allShots, currentSeriesGlobal, currentCompetitorIndex, startingTarget, competitionId, isClosed]);

    // --- HELPERS ---
    const currentCompetitor = competitors[currentCompetitorIndex];

    const getPossibleValues = (comp) => {
        if (!comp) return [];
        // JAVÍTÁS: vessző mentén darabolunk, ahogy az app ParsedQrData.kt fájljában van
        return comp.rawPossibleHitValuesString.split(',').map(v => v.trim()).filter(v => v);
    };

    const getScoreValue = (comp, val) => {
        if (val.toUpperCase() === 'X') return 10;
        if (val.toUpperCase() === 'M') return 0;
        const num = parseInt(val);
        if (!isNaN(num)) return num;
        return 0;
    };

    const getActualTargetNumber = (logicalSeries, start, total) => {
        if (!total) return 1;
        const actualTargetZeroBased = (start - 1 + logicalSeries - 1) % total;
        return actualTargetZeroBased + 1;
    };

    const padToLength = (str, length, char = '0') => {
        let s = String(str);
        while (s.length < length) s = char + s;
        return s.slice(-length);
    };

    const MAX_COMPETITORS_PER_QR = 2;

    const generateSingleQrStringForChunk = (chunkCompetitors) => {
        let qr = "02";
        qr += "1";

        const totalTargets = chunkCompetitors[0].targetCount;
        qr += padToLength(totalTargets, 3);

        const firstCompValues = getPossibleValues(chunkCompetitors[0]);
        const scoreValueStrings = firstCompValues
            .map(v => padToLength(getScoreValue(chunkCompetitors[0], v), 2))
            .sort((a, b) => b - a);

        const positiveCount = scoreValueStrings.filter(v => parseInt(v) > 0).length;
        qr += positiveCount.toString(16);

        scoreValueStrings.forEach(v => qr += v);
        qr += padToLength(startingTarget, 3);

        chunkCompetitors.forEach(comp => {
            const rawId = comp.competitorId.trim();
            const participantId = padToLength(rawId, 10);
            qr += participantId;
            qr += padToLength(comp.shotsPerTarget, 2);

            const compShots = allShots[comp.competitorId] || [];

            for (let l = 1; l <= comp.targetCount; l++) {
                const tgt = getActualTargetNumber(l, startingTarget, comp.targetCount);
                for (let sPos = 1; sPos <= comp.shotsPerTarget; sPos++) {
                    const shot = compShots.find(s => s.target === tgt && s.position === sPos.toString());
                    let scoreToFind = "00";
                    if (shot && shot.value.toUpperCase() !== 'M' && shot.value !== "") {
                        scoreToFind = padToLength(shot.sum, 2);
                    }

                    let scoreIndex = scoreValueStrings.indexOf(scoreToFind);
                    if (scoreIndex === -1 || scoreIndex > 15) {
                        const numVal = parseInt(scoreToFind) || 0;
                        if (numVal > 0) {
                            let closestIdx = 0;
                            let minDiff = 999;
                            scoreValueStrings.forEach((v, idx) => {
                                const diff = Math.abs(parseInt(v) - numVal);
                                if (diff < minDiff) {
                                    minDiff = diff;
                                    closestIdx = idx;
                                }
                            });
                            scoreIndex = closestIdx;
                        } else {
                            scoreIndex = scoreValueStrings.indexOf("00");
                            if (scoreIndex === -1) scoreIndex = 0;
                        }
                    }
                    qr += scoreIndex.toString(16);
                }
            }
        });

        return qr;
    };

    const generateQrStrings = () => {
        if (competitors.length === 0) return [];

        if (competitors.length <= MAX_COMPETITORS_PER_QR) {
            return [generateSingleQrStringForChunk(competitors)];
        }

        const chunks = [];
        for (let i = 0; i < competitors.length; i += MAX_COMPETITORS_PER_QR) {
            chunks.push(competitors.slice(i, i + MAX_COMPETITORS_PER_QR));
        }

        const totalParts = chunks.length;
        const allowedChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let sessionHash = '';
        for (let i = 0; i < 4; i++) {
            sessionHash += allowedChars.charAt(Math.floor(Math.random() * allowedChars.length));
        }

        return chunks.map((chunkCompetitors, index) => {
            const partIndex = index + 1;
            let qr = "03";
            qr += sessionHash;
            qr += padToLength(totalParts, 2);
            qr += padToLength(partIndex, 2);
            qr += "1";

            const totalTargets = chunkCompetitors[0].targetCount;
            qr += padToLength(totalTargets, 3);

            const firstCompValues = getPossibleValues(chunkCompetitors[0]);
            const scoreValueStrings = firstCompValues
                .map(v => padToLength(getScoreValue(chunkCompetitors[0], v), 2))
                .sort((a, b) => b - a);

            const positiveCount = scoreValueStrings.filter(v => parseInt(v) > 0).length;
            qr += positiveCount.toString(16);

            scoreValueStrings.forEach(v => qr += v);
            qr += padToLength(startingTarget, 3);

            chunkCompetitors.forEach(comp => {
                const rawId = comp.competitorId.trim();
                const participantId = padToLength(rawId, 10);
                qr += participantId;
                qr += padToLength(comp.shotsPerTarget, 2);

                const compShots = allShots[comp.competitorId] || [];

                for (let l = 1; l <= comp.targetCount; l++) {
                    const tgt = getActualTargetNumber(l, startingTarget, comp.targetCount);
                    for (let sPos = 1; sPos <= comp.shotsPerTarget; sPos++) {
                        const shot = compShots.find(s => s.target === tgt && s.position === sPos.toString());
                        let scoreToFind = "00";
                        if (shot && shot.value.toUpperCase() !== 'M' && shot.value !== "") {
                            scoreToFind = padToLength(shot.sum, 2);
                        }

                        const scoreIndex = scoreValueStrings.indexOf(scoreToFind);
                        if (scoreIndex === -1 || scoreIndex > 15) {
                            const zeroIndex = scoreValueStrings.indexOf("00");
                            qr += (zeroIndex !== -1 ? zeroIndex.toString(16) : "0");
                        } else {
                            qr += scoreIndex.toString(16);
                        }
                    }
                }
            });

            return qr;
        });
    };

    const handleShowQr = () => {
        const strings = generateQrStrings();
        setQrStringsList(strings);
        setCurrentQrIndex(0);
        setShowQrModal(true);
    };

    useEffect(() => {
        if (!showQrModal || qrStringsList.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentQrIndex(prev => (prev + 1) % qrStringsList.length);
        }, qrSpeedMs);

        return () => clearInterval(interval);
    }, [showQrModal, qrStringsList.length, qrSpeedMs]);

    const getColorForZoneValue = (value) => {
        const num = parseInt(value);
        if (value.toUpperCase() === 'X' || num > 10) return '#4CAF50'; // Green
        if (value === '10' || value === '9') return '#FFD700'; // Gold
        if (value === '8' || value === '7') return '#E53935'; // Red (MISZ)
        if (value === '6' || value === '5') return '#2196F3'; // Blue
        return '#9E9E9E'; // Gray
    };

    // --- NAVIGATION ---
    const changeCompetitor = (dir) => {
        const newIdx = currentCompetitorIndex + dir;
        if (newIdx >= 0 && newIdx < competitors.length) {
            setCurrentCompetitorIndex(newIdx);
        }
    };

    const changeSeries = (dir) => {
        const newSeries = currentSeriesGlobal + dir;

        if (dir === 1) {
            const everyoneFinished = competitors.every(c => {
                const shots = allShots[c.competitorId] || [];
                const actualTgt = getActualTargetNumber(currentSeriesGlobal, startingTarget, c.targetCount);
                const shotsInThisTgt = shots.filter(s => s.target === actualTgt && s.value !== "").length;
                return shotsInThisTgt >= c.shotsPerTarget;
            });

            if (!everyoneFinished) {
                alert("Még nem mindenki fejezte be ezt a sorozatot!");
                return;
            }

            const maxTgt = Math.max(...competitors.map(c => c.targetCount));
            if (newSeries <= maxTgt) {
                setCurrentSeriesGlobal(newSeries);
                setCurrentCompetitorIndex(0);
            }
        } else if (newSeries >= 1) {
            setCurrentSeriesGlobal(newSeries);
            setCurrentCompetitorIndex(0);
        }
    };

    // --- SCORING ---
    const recordShot = (value) => {
        if (isClosed) return;

        const comp = currentCompetitor;
        const actualTgt = getActualTargetNumber(currentSeriesGlobal, startingTarget, comp.targetCount);
        const shots = [...(allShots[comp.competitorId] || [])];

        const shotsInThisTgt = shots.filter(s => s.target === actualTgt);
        if (shotsInThisTgt.length >= comp.shotsPerTarget) {
            return;
        }

        const newShot = {
            value: value,
            target: actualTgt,
            sum: getScoreValue(comp, value),
            position: (shotsInThisTgt.length + 1).toString()
        };

        setAllShots(prev => ({
            ...prev,
            [comp.competitorId]: [...(prev[comp.competitorId] || []), newShot]
        }));
    };

    const handleShotClick = (indexInSeries) => {
        if (isClosed) return;
        const comp = currentCompetitor;
        const actualTgt = getActualTargetNumber(currentSeriesGlobal, startingTarget, comp.targetCount);
        const shots = [...(allShots[comp.competitorId] || [])];

        // Find the shot at the given position in the current target
        const targetShots = shots.filter(s => s.target === actualTgt);
        if (indexInSeries < targetShots.length) {
            const shotToRemove = targetShots[indexInSeries];
            const realIdx = shots.indexOf(shotToRemove);
            if (realIdx !== -1) {
                shots.splice(realIdx, 1);
                setAllShots(prev => ({ ...prev, [comp.competitorId]: shots }));
            }
        }
    };

    const centerOnMe = () => {
        if (userLocation && mapRef.current) {
            mapRef.current.panTo(userLocation);
            mapRef.current.setZoom(18);
        } else if (eventLocationFallback && mapRef.current) {
            mapRef.current.panTo(eventLocationFallback);
            mapRef.current.setZoom(17);
        }
    };

    // --- STATS & DATA ---
    const stats = useMemo(() => {
        if (!currentCompetitor) return { total: 0, avg: 0, zones: {}, diff: "", chartData: [], history: [] };

        const shots = allShots[currentCompetitor.competitorId] || [];
        const filledShots = shots.filter(s => s.value !== "");
        const total = filledShots.reduce((sum, s) => sum + s.sum, 0);
        const count = filledShots.length;
        const avg = count > 0 ? (total / count).toFixed(2) : "0.00";

        // Zónák
        const zones = {};
        const possibleValues = getPossibleValues(currentCompetitor);
        possibleValues.forEach(v => zones[v.toUpperCase()] = 0);
        filledShots.forEach(s => {
            const v = s.value.toUpperCase();
            if (zones[v] !== undefined) zones[v]++;
        });

        // Eltérés (Difference) - Android app logika alapján:
        const numericValues = possibleValues.map(v => parseInt(v)).filter(n => !isNaN(n));
        const naturalMax = numericValues.length > 0 ? Math.max(...numericValues) : 0;

        let referenceMax = 10; // Alapértelmezett (pl. WA)
        if (naturalMax > 10) {
            referenceMax = 10; // Ha van 11-es, akkor is a 10-est tekintjük alapnak
        } else if (naturalMax > 0) {
            referenceMax = naturalMax; // Pl. IFAA esetén 5
        }

        const expectedMaxSoFar = count * referenceMax;
        const diffValue = total - expectedMaxSoFar;
        const diff = count > 0 ? ` (${diffValue > 0 ? '+' : ''}${diffValue})` : "";

        // Chart Data (Running Average)
        let runningSum = 0;
        const chartData = filledShots.map((s, idx) => {
            runningSum += s.sum;
            return {
                name: idx + 1,
                avg: (runningSum / (idx + 1)).toFixed(2)
            };
        });

        // History
        const history = [];
        let cumulative = 0;
        for (let l = 1; l <= currentCompetitor.targetCount; l++) {
            const tgt = getActualTargetNumber(l, startingTarget, currentCompetitor.targetCount);
            const seriesShots = shots.filter(s => s.target === tgt);
            if (seriesShots.length > 0) {
                const seriesScore = seriesShots.reduce((sum, s) => sum + s.sum, 0);
                cumulative += seriesScore;
                history.push({
                    target: tgt,
                    values: seriesShots.map(s => s.value),
                    score: seriesScore,
                    cumulative: cumulative
                });
            }
        }

        return { total, avg, count, referenceMax, zones, diff, chartData, history };
    }, [currentCompetitor, allShots, startingTarget]);

    const areAllShotsRecorded = () => {
        if (competitors.length === 0) return false;
        return competitors.every(c => {
            const expected = c.targetCount * c.shotsPerTarget;
            const actual = (allShots[c.competitorId] || []).filter(s => s.value !== "").length;
            return actual >= expected;
        });
    };

    // --- FIREBASE FINALIZE (SAFARI BLOKKOLÁS ELLENI EGYEDI MODALLAL) ---
    const handleOpenFinalizeModal = () => {
        if (!auth.currentUser) {
            alert("Be kell jelentkezned a mentéshez!");
            return;
        }
        setShowFinalizeConfirmModal(true);
    };

    const handleConfirmFinalize = async () => {
        setShowFinalizeConfirmModal(false);
        try {
            const compId = competitionId || doc(collection(db, "users", auth.currentUser.uid, "competitions")).id;

            const competitorsData = competitors.map(c => {
                const shots = allShots[c.competitorId] || [];
                const total = shots.reduce((sum, s) => sum + s.sum, 0);
                return {
                    ...c,
                    shots: shots,
                    totalScore: total,
                    averageArrowScore: shots.length > 0 ? total / shots.length : 0
                };
            });

            const competitionData = {
                competitionName: `Verseny - ${new Date().toLocaleDateString()}`,
                status: "completed",
                currentSeriesGlobal,
                startingTargetNumber: startingTarget,
                competitors: competitorsData,
                lastUpdated: serverTimestamp(),
                dateCreated: serverTimestamp(),
                overallTotalScore: competitorsData.reduce((sum, c) => sum + c.totalScore, 0)
            };

            await setDoc(doc(db, "users", auth.currentUser.uid, "competitions", compId), competitionData);

            setIsClosed(true);
            setCompetitionId(compId);

            // Megnyitjuk a QR-kódos modalt mentés után automatikusan
            const strings = generateQrStrings();
            setQrStringsList(strings);
            setCurrentQrIndex(0);
            setShowQrModal(true);
        } catch (e) {
            console.error("Firebase save error", e);
            alert("Hiba történt a mentés során: " + e.message);
        }
    };

    if (!currentCompetitor) return <div style={loadingStyle}>Betöltés...</div>;

    const actualTgt = getActualTargetNumber(currentSeriesGlobal, startingTarget, currentCompetitor.targetCount);
    const shotsInCurrentTgt = (allShots[currentCompetitor.competitorId] || []).filter(s => s.target === actualTgt);

    return (
        <div style={pageContainerStyle}>
            {/* 1. NAVIGÁCIÓS KÁRTYA */}
            <div style={cardStyle}>
                {/* Sorozat Sor */}
                <div style={navRowStyle}>
                    <button onClick={() => changeSeries(-1)} disabled={currentSeriesGlobal === 1} style={iconBtnStyle}>
                        <MdChevronLeft size={32} color={currentSeriesGlobal === 1 ? "#ccc" : "#b30000"} />
                    </button>
                    <div style={headerTextStyle}>
                        {actualTgt} / {currentCompetitor.targetCount} Cél
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <button onClick={() => changeSeries(1)} style={iconBtnStyle}>
                            <MdChevronRight size={32} color="#b30000" />
                        </button>

                        {/* Akciógomb (Mentés vagy QR) */}
                        {(areAllShotsRecorded() || isClosed) && (
                            <button
                                onClick={isClosed ? handleShowQr : handleOpenFinalizeModal}
                                style={actionBtnStyle}
                            >
                                {isClosed ? <MdQrCodeScanner size={20} /> : <MdSave size={20} />}
                            </button>
                        )}
                    </div>
                </div>

                {/* ÚJ: CÉLOK HALADÁSI SÁVJA (App Parity) */}
                <div style={{
                  display: 'flex',
                  gap: '2px',
                  justifyContent: 'center',
                  padding: '5px 16px 10px 16px',
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                  {Array.from({ length: currentCompetitor.targetCount }).map((_, i) => {
                    const targetNum = i + 1;
                    const isCurrent = targetNum === actualTgt;
                    const shots = allShots[currentCompetitor.competitorId] || [];
                    const shotsInThisTgt = shots.filter(s => s.target === targetNum && s.value !== "").length;
                    const isCompleted = shotsInThisTgt >= currentCompetitor.shotsPerTarget;

                    let color = '#444'; // Üres (Sötétszürke)
                    if (isCurrent) {
                      color = '#b30000'; // Aktuális (Piros)
                    } else if (isCompleted) {
                      color = '#ffcdd2'; // Kész (Halvány piros)
                    }

                    return (
                      <div
                        key={i}
                        style={{
                          flex: 1,
                          height: '14px',
                          backgroundColor: color,
                          borderRadius: '2px',
                          transition: 'background-color 0.3s ease'
                        }}
                      />
                    );
                  })}
                </div>

                <div style={dividerStyle} />

                {/* Versenyző Sor */}
                <div style={navRowStyle}>
                    <button onClick={() => changeCompetitor(-1)} disabled={currentCompetitorIndex === 0} style={iconBtnStyle}>
                        <MdChevronLeft size={32} color={currentCompetitorIndex === 0 ? "#ccc" : "#b30000"} />
                    </button>
                    <div style={{ textAlign: 'center', flex: 1 }}>
                        <div style={nameTextStyle}>{currentCompetitor.competitorName}</div>
                        <div style={subTextStyle}>
                          {currentCompetitor.bowCategory === "Azonosító:" ? `(ID: ${currentCompetitor.competitorId})` : `${currentCompetitor.bowCategory} - ${currentCompetitor.ageCategory}`}
                        </div>
                    </div>
                    <button onClick={() => changeCompetitor(1)} disabled={currentCompetitorIndex === competitors.length - 1} style={iconBtnStyle}>
                        <MdChevronRight size={32} color={currentCompetitorIndex === competitors.length - 1 ? "#ccc" : "#b30000"} />
                    </button>
                </div>
            </div>

            {/* GÖRGETHETŐ TARTALOM */}
            <div style={scrollContentStyle}>

                {/* LÖVÉSEK KÁRTYA */}
                <div style={cardStyle}>
                    <div style={shotsRowStyle}>
                        {Array.from({ length: currentCompetitor.shotsPerTarget }).map((_, i) => {
                            const val = shotsInCurrentTgt[i]?.value || "-";
                            const isActive = val !== "-";
                            return (
                                <div
                                    key={i}
                                    style={shotBoxStyle(isActive)}
                                    onClick={() => handleShotClick(i)}
                                >
                                    {val}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* GOMBPANEL KÁRTYA */}
                {!isClosed && (
                    <div style={cardStyle}>
                        <div style={flexboxStyle}>
                            {getPossibleValues(currentCompetitor).sort((a,b) => {
                                const scoreA = getScoreValue(currentCompetitor, a);
                                const scoreB = getScoreValue(currentCompetitor, b);
                                return scoreB - scoreA;
                            }).map(val => (
                                <button
                                    key={val}
                                    onClick={() => recordShot(val)}
                                    style={{
                                        ...hitButtonStyle,
                                        backgroundColor: '#FFCDD2', // Halvány piros
                                        color: '#B71C1C', // Sötétvörös szöveg a kontraszt miatt
                                        border: '1px solid #EF9A9A'
                                    }}
                                >
                                    {val}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* STATISZTIKA PANEL */}
                <div style={cardStyle}>
                    <div style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', alignItems: 'center' }}>
                            <div style={statLabelStyle}>PONTOK: <span style={statValStyle}>{stats.total}{stats.diff}</span></div>
                            <div
                                onClick={() => setIsAverageInPercentage(!isAverageInPercentage)}
                                style={{
                                    backgroundColor: '#b30000',
                                    color: 'white',
                                    padding: '4px 12px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    fontSize: '0.85rem',
                                    display: 'inline-block',
                                    boxShadow: '0 2px 5px rgba(179,0,0,0.2)',
                                    userSelect: 'none'
                                }}
                                title="Kattints az átlag / százalék váltáshoz"
                            >
                                ÁTLAG: {isAverageInPercentage && stats.count > 0 ? (
                                    `${((parseFloat(stats.avg) / stats.referenceMax) * 100).toFixed(1)}%`
                                ) : (
                                    stats.avg
                                )}
                            </div>
                        </div>

                        <div style={zonesContainerStyle}>
                            {Object.entries(stats.zones).sort((a, b) => {
                                const scoreA = getScoreValue(currentCompetitor, a[0]);
                                const scoreB = getScoreValue(currentCompetitor, b[0]);
                                return scoreB - scoreA;
                            }).map(([val, count]) => (
                                <div key={val} style={zoneItemStyle}>
                                    <div style={zoneLabelStyle(val)}>{val}</div>
                                    <div style={zoneCountStyle}>{count}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* GRAFIKON KÁRTYA */}
                <div style={{ ...cardStyle, height: '200px', padding: '10px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={stats.chartData} margin={{ top: 5, right: 10, left: -25, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                            <XAxis
                                dataKey="name"
                                fontSize={10}
                                tickLine={false}
                                axisLine={false}
                                interval={Math.floor(stats.chartData.length / 10)}
                            />
                            <YAxis
                                domain={[0, 'auto']}
                                fontSize={10}
                                tickLine={false}
                                axisLine={false}
                            />
                            <Tooltip />
                            <Line
                                type="monotone"
                                dataKey="avg"
                                stroke="#4CAF50"
                                strokeWidth={2}
                                dot={{ r: 2 }}
                                isAnimationActive={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* ELŐZMÉNYEK KÁRTYA */}
                <div style={cardStyle}>
                    <div
                        style={historyHeaderStyle}
                        onClick={() => setShowHistory(!showHistory)}
                    >
                        <span style={historyTitleStyle}>Találatok (lista nézet)</span>
                        {showHistory ? <MdExpandLess size={24} color="white" /> : <MdExpandMore size={24} color="white" />}
                    </div>

                    {showHistory && (
                        <div style={historyListStyle}>
                            {stats.history.length === 0 ? (
                                <div style={{ padding: '15px', textAlign: 'center', color: '#888' }}>Még nincsenek lövések.</div>
                            ) : (
                                stats.history.map((h, idx) => (
                                    <div key={idx} style={historyItemStyle}>
                                        <div style={historyTargetStyle}>{h.target}. Cél</div>
                                        <div style={historyShotsStyle}>
                                            {h.values.map((v, i) => (
                                                <span key={i} style={historyShotBadgeStyle(v)}>{v}</span>
                                            ))}
                                        </div>
                                        <div style={historyScoreStyle}>{h.score} ({h.cumulative})</div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                {/* VISSZA GOMB */}
                <div style={{ padding: '0 10px', marginTop: '10px' }}>
                    <button onClick={() => navigate('/')} style={backButtonStyle}>Vissza a Főoldalra</button>
                </div>

            </div>

            {/* QR KÓD MODAL (MOBILRA IS OPTIMALIZÁLT) */}
            {showQrModal && (
                <div style={qrModalOverlayStyle}>
                    <div style={{ ...qrModalContentStyle, maxWidth: '360px', width: '92%', padding: '16px', boxSizing: 'border-box' }}>
                        <div style={qrModalHeaderStyle}>
                            <span style={{ fontWeight: 'bold', color: '#b30000', fontSize: '1.1rem' }}>Verseny Eredmény QR</span>
                            <MdClose size={24} onClick={() => setShowQrModal(false)} style={{ cursor: 'pointer', color: '#666' }} />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0' }}>
                            <QRCodeCanvas
                                value={qrStringsList[currentQrIndex] || ""}
                                size={Math.min(window.innerWidth - 60, 270)}
                                level="M"
                                marginSize={1}
                            />
                        </div>

                        {/* SEBESSÉGVÁLASZTÓ GOMBOK */}
                        {qrStringsList.length > 1 && (
                            <div style={{ marginTop: '15px' }}>
                                <div style={{ fontSize: '0.85rem', color: '#555', fontWeight: 'bold', marginBottom: '8px', textAlign: 'center' }}>
                                    Beolvasási sebesség
                                </div>
                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                    <button
                                        onClick={() => setQrSpeedMs(350)}
                                        style={{
                                            flex: 1,
                                            padding: '8px 4px',
                                            borderRadius: '8px',
                                            border: '1px solid #b30000',
                                            backgroundColor: qrSpeedMs === 350 ? '#b30000' : 'white',
                                            color: qrSpeedMs === 350 ? 'white' : '#b30000',
                                            fontWeight: 'bold',
                                            fontSize: '0.8rem',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Villám
                                    </button>
                                    <button
                                        onClick={() => setQrSpeedMs(500)}
                                        style={{
                                            flex: 1,
                                            padding: '8px 4px',
                                            borderRadius: '8px',
                                            border: '1px solid #b30000',
                                            backgroundColor: qrSpeedMs === 500 ? '#b30000' : 'white',
                                            color: qrSpeedMs === 500 ? 'white' : '#b30000',
                                            fontWeight: 'bold',
                                            fontSize: '0.8rem',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Normál
                                    </button>
                                    <button
                                        onClick={() => setQrSpeedMs(750)}
                                        style={{
                                            flex: 1,
                                            padding: '8px 4px',
                                            borderRadius: '8px',
                                            border: '1px solid #b30000',
                                            backgroundColor: qrSpeedMs === 750 ? '#b30000' : 'white',
                                            color: qrSpeedMs === 750 ? 'white' : '#b30000',
                                            fontWeight: 'bold',
                                            fontSize: '0.8rem',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Biztonsági
                                    </button>
                                </div>
                            </div>
                        )}

                        <div style={{ marginTop: '15px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
                            Olvasd be az ArcheryNexus versenyirodával az eredmények rögzítéséhez!
                        </div>

                        <button onClick={() => setShowQrModal(false)} style={{ ...finalizeButtonStyle, marginTop: '15px' }}>Bezárás</button>
                    </div>
                </div>
            )}

            {/* 4. TÉRKÉP GOMB TOLDALÉK (CSAK ITT) */}
            {!showMap && courseMapData && (courseMapData.paths?.length > 0 || courseMapData.targets?.length > 0) && (
              <div
                style={mapExtensionStyle}
                onClick={() => setShowMap(true)}
              >
                <MdMap size={24} style={{ marginBottom: '2px' }} />
                <span style={{ fontSize: '10px', fontWeight: 'bold' }}>TÉRKÉP</span>
              </div>
            )}

            {/* 5. TÉRKÉP OVERLAY */}
            {showMap && (
                <div style={mapOverlayStyle}>
                    <div style={mapHeaderStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <MdClose
                                size={28}
                                onClick={handleCloseMap}
                                style={{ cursor: 'pointer', color: '#fff', backgroundColor: '#b30000', borderRadius: '50%', padding: '2px' }}
                            />
                            <span style={{ fontWeight: 'bold', color: '#333' }}>Pályatérkép</span>
                        </div>
                        {isSearchingEvent && <span style={{ fontSize: '12px', color: '#b30000' }}>Keresés...</span>}
                    </div>

                    <div style={{ flex: 1, position: 'relative' }}>
                        <Map
                            defaultCenter={userLocation || eventLocationFallback || { lat: 47.1625, lng: 19.5033 }}
                            defaultZoom={17}
                            mapId="bf51a910020fa566"
                            mapTypeId="satellite"
                            disableDefaultUI={true}
                            onIdle={(e) => { mapRef.current = e.map; }}
                        >
                            {/* Felhasználó pozíciója */}
                            {userLocation && (
                                <AdvancedMarker position={userLocation}>
                                    <div style={userDotStyle} />
                                </AdvancedMarker>
                            )}

                            {/* Útvonalak */}
                            {courseMapData?.paths?.map((path, idx) => (
                                <Polyline
                                    key={idx}
                                    path={path.points.map(p => ({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) }))}
                                    strokeColor={path.color || '#0000FF'}
                                />
                            ))}

                            {/* Célok */}
                            {courseMapData?.targets?.map((target, idx) => {
                                const id = parseInt(target.id) || 0;
                                const tLat = parseFloat(target.lat);
                                const tLng = parseFloat(target.lng);
                                if (isNaN(tLat) || isNaN(tLng)) return null;

                                return (
                                    <AdvancedMarker key={idx} position={{ lat: tLat, lng: tLng }}>
                                        <div style={targetCircleStyle}>{id}</div>
                                    </AdvancedMarker>
                                );
                            })}

                            {/* Speciális pontok */}
                            {courseMapData?.specialMarkers && Object.entries(courseMapData.specialMarkers).map(([key, data]) => {
                                const title = key === 'office' ? 'Versenyiroda' : (key === 'warmup' ? 'Belövő pálya' : key);
                                const color = key === 'office' ? '#007FFF' : (key === 'warmup' ? '#00CED1' : '#FFD700');
                                const sLat = parseFloat(data.lat);
                                const sLng = parseFloat(data.lng);
                                if (isNaN(sLat) || isNaN(sLng)) return null;

                                return (
                                    <AdvancedMarker key={key} position={{ lat: sLat, lng: sLng }}>
                                        <div style={specialBubbleStyle(color)}>{title}</div>
                                    </AdvancedMarker>
                                );
                            })}
                        </Map>

                        {/* Centerezés gomb */}
                        <button
                            onClick={centerOnMe}
                            style={centerBtnStyle}
                        >
                            <MdMyLocation size={24} color="#b30000" />
                        </button>
                    </div>
                </div>
            )}

            {/* --- MEGERŐSÍTŐ MODAL A LEZÁRÁSHOZ (SAFARI BLOKKOLÁS ELLEN) --- */}
            {showFinalizeConfirmModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    backdropFilter: 'blur(3px)',
                    zIndex: 10005,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '15px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        padding: '24px',
                        maxWidth: '400px',
                        width: '100%',
                        textAlign: 'center',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.25)'
                    }}>
                        <div style={{
                            backgroundColor: '#b30000',
                            color: 'white',
                            padding: '12px',
                            margin: '-24px -24px 20px -24px',
                            borderRadius: '16px 16px 0 0',
                            fontWeight: 'bold',
                            fontSize: '1.1rem'
                        }}>
                            Verseny lezárása és mentése 🏹
                        </div>
                        <p style={{ color: '#333', fontSize: '0.95rem', lineHeight: '1.5', marginBottom: '24px' }}>
                            Biztosan lezárod és mented a versenyt? <br/>
                            <strong>Ezután a pontszámok már nem módosíthatók!</strong>
                        </p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <button
                                type="button"
                                onClick={() => setShowFinalizeConfirmModal(false)}
                                style={{
                                    flex: 1,
                                    padding: '10px',
                                    border: 'none',
                                    backgroundColor: '#eee',
                                    color: '#555',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer'
                                }}
                            >
                                Mégse
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmFinalize}
                                style={{
                                    flex: 1,
                                    padding: '10px',
                                    border: 'none',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 6px rgba(40,167,69,0.3)'
                                }}
                            >
                                Igen, lezárom
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- STÍLUSOK (APP DESIGN PARITÁS) ---
const loadingStyle = { textAlign: 'center', padding: '100px', color: '#b30000', fontWeight: 'bold' };

const pageContainerStyle = {
    backgroundColor: '#f0f0f0',
    minHeight: '100vh',
    fontFamily: 'Roboto, Arial, sans-serif',
    display: 'flex',
    flexDirection: 'column'
};

const cardStyle = {
    backgroundColor: 'white',
    margin: '8px',
    borderRadius: '12px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    overflow: 'hidden'
};

const navRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px'
};

const dividerStyle = {
    height: '1px',
    backgroundColor: '#eee',
    margin: '0 16px'
};

const headerTextStyle = {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
    textAlign: 'center'
};

const nameTextStyle = {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333'
};

const subTextStyle = {
    fontSize: '14px',
    color: '#666'
};

const iconBtnStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center'
};

const actionBtnStyle = {
    backgroundColor: '#b30000',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '8px',
    cursor: 'pointer'
};

const scrollContentStyle = {
    flex: 1,
    overflowY: 'auto',
    paddingBottom: '20px'
};

const shotsRowStyle = {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    minHeight: '60px'
};

const shotBoxStyle = (active) => ({
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 'bold',
    border: active ? '1px solid #333' : '1px dashed #ccc',
    backgroundColor: active ? '#b30000' : '#fff',
    color: active ? '#fff' : '#ccc',
    cursor: active ? 'pointer' : 'default'
});

const flexboxStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    padding: '12px',
    gap: '8px'
};

const hitButtonStyle = {
    width: '60px',
    height: '50px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    backgroundColor: '#fff',
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333',
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
};

const statLabelStyle = {
    fontSize: '16px',
    color: '#666',
    fontWeight: 'bold'
};

const statValStyle = {
    color: '#333',
    fontSize: '18px'
};

const zonesContainerStyle = {
    display: 'flex',
    justifyContent: 'center',
    gap: '4px',
    flexWrap: 'wrap'
};

const zoneItemStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '32px'
};

const zoneLabelStyle = (val) => {
    const num = parseInt(val);
    let color = '#9E9E9E';
    if (val.toUpperCase() === 'X' || num > 10) color = '#4CAF50';
    else if (val === '10' || val === '9') color = '#FFD700';
    else if (val === '8' || val === '7') color = '#E53935';
    else if (val === '6' || val === '5') color = '#1E88E5';

    return {
        backgroundColor: color,
        color: '#fff',
        fontSize: '10px',
        fontWeight: 'bold',
        padding: '2px 6px',
        borderRadius: '4px',
        minWidth: '24px',
        textAlign: 'center'
    };
};

const zoneCountStyle = {
    fontSize: '14px',
    fontWeight: 'bold',
    marginTop: '2px'
};

const historyHeaderStyle = {
    backgroundColor: '#b30000',
    padding: '10px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer'
};

const historyTitleStyle = {
    color: 'white',
    fontSize: '18px',
    fontWeight: 'bold'
};

const historyListStyle = {
    padding: '8px 0'
};

const historyItemStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    borderBottom: '1px solid #eee'
};

const historyTargetStyle = {
    width: '60px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#666'
};

const historyShotsStyle = {
    flex: 1,
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
    padding: '0 8px'
};

const historyShotBadgeStyle = (val) => {
    const num = parseInt(val);
    let color = '#9E9E9E';
    if (val.toUpperCase() === 'X' || num > 10) color = '#4CAF50';
    else if (val === '10' || val === '9') color = '#FFD700';
    else if (val === '8' || val === '7') color = '#E53935';
    else if (val === '6' || val === '5') color = '#1E88E5';

    return {
        backgroundColor: color,
        color: 'white',
        fontSize: '11px',
        padding: '2px 6px',
        borderRadius: '4px',
        minWidth: '20px',
        textAlign: 'center'
    };
};

const historyScoreStyle = {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#333',
    minWidth: '70px',
    textAlign: 'right'
};

const backButtonStyle = {
    width: '100%',
    padding: '12px',
    backgroundColor: '#fff',
    color: '#666',
    border: '1px solid #ddd',
    borderRadius: '12px',
    fontSize: '14px',
    cursor: 'pointer'
};

const finalizeButtonStyle = {
    width: '100%',
    padding: '12px',
    backgroundColor: '#b30000',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontWeight: 'bold',
    marginTop: '20px',
    cursor: 'pointer'
};

const qrModalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 5000
};

const qrModalContentStyle = {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '15px',
    width: '90%',
    maxWidth: '350px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
};

const qrModalHeaderStyle = {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
};

const qrContainerStyle = {
    backgroundColor: 'white',
    padding: '10px',
    borderRadius: '10px',
    boxShadow: '0 0 10px rgba(0,0,0,0.1)'
};

// --- MAP SPECIFIC STYLES ---
const mapExtensionStyle = {
    position: 'fixed',
    bottom: '60px',
    right: 0,
    width: '37.5%',
    height: '60px',
    backgroundColor: '#b30000',
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    clipPath: 'polygon(25% 0%, 100% 0%, 100% 100%, 0% 100%)',
    zIndex: 1900,
    cursor: 'pointer',
    boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
    paddingLeft: '15px'
};

const mapOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'white',
    zIndex: 5000,
    display: 'flex',
    flexDirection: 'column'
};

const mapHeaderStyle = {
    height: '50px',
    padding: '0 15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #ddd',
    boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
};

const centerBtnStyle = {
    position: 'absolute',
    bottom: '20px',
    right: '20px',
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    backgroundColor: 'white',
    border: 'none',
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 10
};

const userDotStyle = {
    width: '16px',
    height: '16px',
    backgroundColor: '#2196F3',
    borderRadius: '50%',
    border: '3px solid white',
    boxShadow: '0 0 5px rgba(0,0,0,0.3)'
};

const targetCircleStyle = {
    width: '32px',
    height: '32px',
    backgroundColor: '#b30000',
    color: 'white',
    borderRadius: '50%',
    border: '2px solid white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '14px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
};

const specialBubbleStyle = (color) => ({
    backgroundColor: color,
    color: 'white',
    padding: '4px 10px',
    borderRadius: '15px',
    border: '2px solid white',
    fontSize: '12px',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
    transform: 'translateY(-10px)'
});

export default CompetitionScoringWeb;
